/**
 * useRelatorioGeraisFilters - Filter state management for general reports
 */
import { useState, useMemo, useCallback } from 'react';
import type { RelatorioFilters } from '../services/relatorioGeraisService';
import { usePersistedState } from './usePersistedState';  // N79

// Default: last 30 days → today
function defaultDateRange() {
  const fim = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  return {
    inicio: inicio.toISOString().slice(0, 10),
    fim: fim.toISOString().slice(0, 10),
  };
}

export function useRelatorioGeraisFilters() {
  const defaults = defaultDateRange();

  // N79: filtros persistidos na sessão (sobrevivem ao "Voltar")
  const [dataInicio, setDataInicio] = usePersistedState('relgerais_f_data_inicio', defaults.inicio);
  const [dataFim, setDataFim] = usePersistedState('relgerais_f_data_fim', defaults.fim);
  const [selectedHospitals, setSelectedHospitals] = usePersistedState<number[]>('relgerais_f_hospitais', []);
  const [selectedClientes, setSelectedClientes] = usePersistedState<number[]>('relgerais_f_clientes', []);
  const [selectedAuditores, setSelectedAuditores] = usePersistedState<number[]>('relgerais_f_auditores', []);
  const [selectedStatus, setSelectedStatus] = usePersistedState<string[]>('relgerais_f_status', []);
  const [selectedGrupos, setSelectedGrupos] = usePersistedState<string[]>('relgerais_f_grupos', []);
  const [selectedFaixaAging, setSelectedFaixaAging] = usePersistedState<string[]>('relgerais_f_aging', []);
  const [allFiltersExpanded, setAllFiltersExpanded] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedHospitals.length) count++;
    if (selectedClientes.length) count++;
    if (selectedAuditores.length) count++;
    if (selectedStatus.length) count++;
    if (selectedGrupos.length) count++;
    if (selectedFaixaAging.length) count++;
    return count;
  }, [selectedHospitals, selectedClientes, selectedAuditores, selectedStatus, selectedGrupos, selectedFaixaAging]);

  const clearAllFilters = useCallback(() => {
    const defaults = defaultDateRange();
    setDataInicio(defaults.inicio);
    setDataFim(defaults.fim);
    setSelectedHospitals([]);
    setSelectedClientes([]);
    setSelectedAuditores([]);
    setSelectedStatus([]);
    setSelectedGrupos([]);
    setSelectedFaixaAging([]);
  }, []);

  const buildApiFilters = useCallback((): RelatorioFilters => {
    return {
      data_inicio: dataInicio,
      data_fim: dataFim,
      hospital_ids: selectedHospitals.length ? selectedHospitals : undefined,
      cliente_ids: selectedClientes.length ? selectedClientes : undefined,
      auditor_ids: selectedAuditores.length ? selectedAuditores : undefined,
      status: selectedStatus.length ? selectedStatus : undefined,
      grupo: selectedGrupos.length ? selectedGrupos : undefined,
      faixa_aging: selectedFaixaAging.length ? selectedFaixaAging : undefined,
    };
  }, [dataInicio, dataFim, selectedHospitals, selectedClientes, selectedAuditores, selectedStatus, selectedGrupos, selectedFaixaAging]);

  return {
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    selectedHospitals,
    setSelectedHospitals,
    selectedClientes,
    setSelectedClientes,
    selectedAuditores,
    setSelectedAuditores,
    selectedStatus,
    setSelectedStatus,
    selectedGrupos,
    setSelectedGrupos,
    selectedFaixaAging,
    setSelectedFaixaAging,
    allFiltersExpanded,
    setAllFiltersExpanded,
    activeFilterCount,
    clearAllFilters,
    buildApiFilters,
  };
}
